import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Send, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useArcheSettings } from '@/lib/hooks/useArcheSettings';
import { HolderSwitcher, useHolderSwitcher } from '@/components/HolderSwitcher';
import { useSelectedHolder } from '../hooks/useSelectedHolder';
import { ArcheHeader } from '../../../components/ArcheHeader';
import {
  useDocumentFiles,
  useEnvelope,
  useUpdateEnvelope,
  useDeleteEnvelope,
  useSetSendMode,
  useFetchFreshEnvelope,
  useDocumentSignFeatures,
} from '../hooks';
import {
  AccessCodeAssignment,
  DocumentGrid,
  DocumentUploader,
  FieldsEditor,
  RecipientsTable,
  type FieldsEditorHandle,
  type RecipientsTableHandle,
  type RecipientSignerSummary,
} from '../components';
import {
  DEFAULT_DOCUMENT_SIGN_SETTINGS,
  DOCUMENT_SIGN_ARCHE_ID,
  PLATFORM_DEFAULT_ENVELOPE_TITLE,
} from '../settings';
import {
  DOCUMENT_SIGN_MAX_RECIPIENTS,
  DOCUMENT_SIGN_MAX_BULK_RECIPIENTS,
  type DocumentSignSigningOrder,
  type DocumentSignAssuranceTier,
  type DocumentSignSendMode,
} from '@/lib/api';
import { tierRequiresAccessCode } from '../assuranceTier';
import { ReturnToOriginButton } from '../components/ReturnToOriginButton';
import { describeSendModeSwitchLosses } from '../sendModeSwitch';

export function EnvelopeBuilderPage() {
  const { envelopeId } = useParams<{ envelopeId: string }>();
  const navigate = useNavigate();
  const { envelope, loading, error } = useEnvelope(envelopeId);
  const updateEnvelope = useUpdateEnvelope(envelopeId ?? '');
  const deleteEnvelope = useDeleteEnvelope();
  const setSendMode = useSetSendMode(envelopeId ?? '');
  const fetchFreshEnvelope = useFetchFreshEnvelope();
  const features = useDocumentSignFeatures();
  const [sendModeError, setSendModeError] = useState<string | null>(null);
  /**
   * True from the moment a mode switch starts flushing until the server has
   * answered. The recipients table, field editor and tier choice are locked
   * for the whole of it: an edit made mid-switch would either be dropped when
   * the table remounts, or leave the field editor holding recipients the
   * switch just deleted — every later save then refused until a reload.
   */
  const [switchingMode, setSwitchingMode] = useState(false);
  /** A switch that would lose work, waiting for the owner to confirm it. */
  const [pendingSwitch, setPendingSwitch] = useState<{
    next: DocumentSignSendMode;
    losses: string[];
  } | null>(null);
  const { files, loading: filesLoading } = useDocumentFiles(envelopeId);

  // The envelope's holder is fixed at creation — surface it read-only. Resolve
  // a friendly display name from the workspace list when it's one the user can
  // still see; otherwise fall back to the holder id.
  const holderSwitcher = useHolderSwitcher();
  const holderName =
    holderSwitcher.options.find((o) => o.holderId === envelope?.holderId)?.displayName ?? null;

  // The global workspace chip shows the user's active workspace preference,
  // not the envelope's fixed holder. Switching the chip on this page updates
  // the platform-wide preference (which Fias 360 / Leads / the envelopes list
  // will respect) but does NOT redirect away from the current envelope —
  // detail / builder pages are keyed by envelopeId, not by the active holder.
  const { holderId: activeHolderId, setHolderId: setActiveHolderId } = useSelectedHolder();

  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [signingOrder, setSigningOrder] = useState<DocumentSignSigningOrder>('parallel');
  const [assuranceTier, setAssuranceTier] = useState<DocumentSignAssuranceTier>('standard');

  /**
   * Access codes for the Regulated tier, and the signer roster they apply to.
   *
   * Held here rather than inside `RecipientsTable` because the inputs live
   * under the tier radio — next to the rule that creates them — while the
   * codes are written by the recipient save. The table publishes its signers
   * up, and reads the codes back down when it persists.
   *
   * Plaintext, and deliberately short-lived: the server stores a scrypt hash
   * and never returns one, so this state IS the only copy until the sender
   * leaves the page.
   */
  const [signers, setSigners] = useState<RecipientSignerSummary[]>([]);
  const [accessCodes, setAccessCodes] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);

  // Navigation flushes pending Step 2 (recipients) and Step 3 (fields) edits —
  // both auto-save on Back / Review rather than via their own buttons.
  const recipientsRef = useRef<RecipientsTableHandle>(null);
  const fieldsRef = useRef<FieldsEditorHandle>(null);

  // Include-filename auto-title (Step 1).
  const [dsSettings] = useArcheSettings(DOCUMENT_SIGN_ARCHE_ID, DEFAULT_DOCUMENT_SIGN_SETTINGS);
  const filesSettledRef = useRef(false);
  const lastSourceCountRef = useRef(0);

  // Seed the form once per envelope. The autosave below invalidates the
  // envelope query on every mutation, so re-seeding on every `envelope`
  // identity change would race against the user's keystrokes: a refetch
  // landing mid-typing would overwrite the input with the just-saved
  // (older) value and drop the in-flight characters. Keying on envelopeId
  // means the form rehydrates only when the route changes to a different
  // envelope, not on background refetches of the same one.
  const hydratedEnvelopeIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!envelope || !envelopeId) return;
    if (hydratedEnvelopeIdRef.current === envelopeId) return;
    hydratedEnvelopeIdRef.current = envelopeId;
    setTitle(envelope.title);
    setMessage(envelope.message ?? '');
    setSigningOrder(envelope.signingOrder);
    setAssuranceTier(envelope.assuranceTier ?? 'standard');
  }, [envelope, envelopeId]);

  // When "Include filename" is on, name the envelope from the first uploaded
  // document — but only while the title is still untouched. The settled-once
  // guard distinguishes a fresh upload from files that loaded with the
  // envelope (which must not retrigger the rename).
  useEffect(() => {
    if (filesLoading) return;
    const sourceFiles = files.filter((f) => f.kind === 'source');
    const count = sourceFiles.length;

    if (!filesSettledRef.current) {
      filesSettledRef.current = true;
      lastSourceCountRef.current = count;
      return;
    }
    const wasEmpty = lastSourceCountRef.current === 0;
    lastSourceCountRef.current = count;
    if (!wasEmpty || count === 0) return;
    if (!dsSettings.includeFilename) return;

    const defaultTitle = dsSettings.defaultTitle.trim();
    // "Untouched" means the owner hasn't manually renamed the envelope
    // yet. Two known un-renamed states: the platform default
    // (`PLATFORM_DEFAULT_ENVELOPE_TITLE`) used when no user setting is
    // configured, and the user's own configured `defaultTitle`. Either
    // is fair game to overwrite with the include-filename auto-title.
    const untouched =
      title === PLATFORM_DEFAULT_ENVELOPE_TITLE || (defaultTitle !== '' && title === defaultTitle);
    if (!untouched) return;

    const baseName = sourceFiles[0].fileName.replace(/\.pdf$/i, '').trim();
    const nextTitle = [defaultTitle, baseName].filter(Boolean).join(' ');
    if (!nextTitle || nextTitle === title) return;

    setTitle(nextTitle);
    void updateEnvelope.mutateAsync({ title: nextTitle });
  }, [files, filesLoading, dsSettings, title, updateEnvelope]);

  // The file-count / total-size caps are on source files only (sealed and
  // certificate files are produced later and don't count against them).
  const sourceFiles = files.filter((f) => f.kind === 'source');

  /*
   * Debounced autosave for title / message / signingOrder. Matches the
   * Step 2 (recipients) and Step 3 (fields) autosave cadence so the entire
   * builder works the same way — no Save button, edits land 700ms after
   * the user stops typing. Imperative `flushSave` (defined below) is
   * flushed on navigation as a fallback for an edit that hasn't yet
   * debounced.
   *
   * Hoisted above the early returns so the hooks-order rule isn't violated
   * when the page first mounts with `envelope` still null — without this,
   * the hook gets registered on the first non-skeleton render and React
   * notices an extra hook on that transition.
   *
   * The initial seed run is suppressed by the ref guard so the values
   * just hydrated from the server response aren't immediately echoed back
   * as a no-op write.
   */
  const autosaveSeededRef = useRef(false);
  useEffect(() => {
    // Inline the draft check — `envelope` may be null on early renders,
    // and we can't use the `isDraft` const (declared further down past
    // the early returns).
    if (!envelopeId || !envelope || envelope.status !== 'draft') return;
    if (!autosaveSeededRef.current) {
      autosaveSeededRef.current = true;
      return;
    }
    const handle = setTimeout(() => {
      void flushSave();
    }, 700);
    return () => clearTimeout(handle);
    // Deliberately deps on the user-editable fields only; the mutation
    // hook identity changes on every render and would defeat the
    // debounce. flushSave reads the live values via closure, so its
    // omission from the dep list is safe.
  }, [title, message, signingOrder, assuranceTier, envelopeId, envelope]);

  if (error) {
    return (
      <div className="flex flex-col h-full">
        <ArcheHeader archeId="arche_document_sign" title="Document Sign">
          <HolderSwitcher selectedHolderId={activeHolderId} onChange={setActiveHolderId} />
        </ArcheHeader>
        <div className="max-w-3xl mx-auto p-6 w-full space-y-3">
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="outline" size="sm" onClick={() => navigate('/a/arche_document_sign')}>
            <ArrowLeft className="h-4 w-4 mr-1" aria-hidden="true" />
            Back to envelopes
          </Button>
        </div>
      </div>
    );
  }

  if (loading || !envelope) {
    return (
      <div className="flex flex-col h-full">
        <ArcheHeader archeId="arche_document_sign" title="Document Sign">
          <HolderSwitcher selectedHolderId={activeHolderId} onChange={setActiveHolderId} />
        </ArcheHeader>
        <div className="max-w-3xl mx-auto p-6 w-full space-y-4">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  const isDraft = envelope.status === 'draft';
  const isBulk = envelope.sendMode === 'bulk';
  // An envelope another app handed over goes to the people that app named;
  // the server refuses to split it into copies, so the choice isn't offered.
  // A copy left unsent by a bulk send belongs to that send's overview — the
  // server refuses to turn it into a bulk send of its own.
  // Offered once bulk send is switched on for this deployment — or always,
  // for a draft that is already a bulk send, so it can be switched back.
  const canChooseSendMode =
    isDraft && !envelope.originArcheId && !envelope.bulkBatchId && (features.bulkSend || isBulk);
  // One shared envelope holds at most DOCUMENT_SIGN_MAX_RECIPIENTS people.
  const bulkListTooLongForSingle =
    isBulk && (envelope.bulkRecipients?.length ?? 0) > DOCUMENT_SIGN_MAX_RECIPIENTS;
  // Bulk copies cannot carry per-person access codes yet.
  const bulkBlockedByTier = tierRequiresAccessCode(assuranceTier);

  const editorsLocked = !isDraft || switchingMode || setSendMode.isPending;

  /**
   * Switch between one shared envelope and a separate copy per person.
   *
   * Everything pending is saved first — title, tier, fields, recipients — so
   * the server converts what the owner actually sees, and a tier change still
   * in its debounce cannot land after the switch and be refused. The losses
   * are then worked out from a FRESH read of the envelope (the cached copy may
   * predate the saves just made); if anything would be lost, the owner is
   * asked first.
   */
  async function handleSendModeChange(next: DocumentSignSendMode) {
    if (!envelopeId || next === envelope?.sendMode) return;
    setSendModeError(null);
    setSwitchingMode(true);
    try {
      if (!(await flushBeforeLeaving())) return;
      const fresh = await fetchFreshEnvelope(envelopeId);
      const losses = describeSendModeSwitchLosses(fresh, next);
      if (losses.length > 0) {
        setPendingSwitch({ next, losses });
        return;
      }
      await applySendMode(next);
    } catch (err) {
      setSendModeError(err instanceof Error ? err.message : 'Could not change how this is sent');
    } finally {
      setSwitchingMode(false);
    }
  }

  async function confirmPendingSwitch() {
    if (!pendingSwitch) return;
    const { next } = pendingSwitch;
    setPendingSwitch(null);
    setSwitchingMode(true);
    try {
      if (!(await flushBeforeLeaving())) return;
      await applySendMode(next);
    } catch (err) {
      setSendModeError(err instanceof Error ? err.message : 'Could not change how this is sent');
    } finally {
      setSwitchingMode(false);
    }
  }

  async function applySendMode(next: DocumentSignSendMode) {
    await setSendMode.mutateAsync(next);
    // The server sets a bulk send to parallel — each copy has one signer.
    // Mirror it, or the next autosave writes the old order straight back.
    if (next === 'bulk') setSigningOrder('parallel');
  }

  // Imperative save flushed on navigation (Back / Review and send) so we
  // don't lose a stale-debounce edit. The debounced autosave below covers
  // the steady-state happy path.
  /** Resolves false (and shows why) when the save failed. */
  async function flushSave(): Promise<boolean> {
    if (!envelopeId) return true;
    setSaveError(null);
    try {
      await updateEnvelope.mutateAsync({
        title: title.trim() || PLATFORM_DEFAULT_ENVELOPE_TITLE,
        message: message.length === 0 ? null : message,
        signingOrder,
        assuranceTier,
      });
      return true;
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
      return false;
    }
  }

  async function handleDelete() {
    if (!envelopeId) return;
    await deleteEnvelope.mutateAsync(envelopeId);
    navigate('/a/arche_document_sign');
  }

  /**
   * Flush everything the builder is holding, and report whether it is safe to
   * leave. Recipient validation can fail — that keeps the user here with the
   * error visible; field placements just flush.
   *
   * EVERY exit from this page goes through this. It exists as one function
   * because it did not, and the second exit — the cross-arche return — simply
   * navigated away, silently discarding field placements the user had just
   * made. A leave path that forgets to flush loses work with no error and no
   * undo, so the flush cannot live at each call site.
   */
  async function flushBeforeLeaving(): Promise<boolean> {
    // Only a draft has edits to save. A sent envelope's editors are read-only,
    // and a save attempted on it is refused — which used to trap the user on
    // the page with no way Back.
    if (envelope?.status !== 'draft') return true;
    // Every save runs (so nothing that can be saved is left behind), and any
    // one failing keeps the user here with its error showing — leaving, or
    // converting the envelope, on top of an edit the server never received
    // would act on stale data.
    const saved = await flushSave();
    const fieldsSaved = (await fieldsRef.current?.save()) ?? true;
    const recipientsSaved = (await recipientsRef.current?.save()) ?? true;
    return saved && fieldsSaved && recipientsSaved;
  }

  async function handleBack() {
    if (await flushBeforeLeaving()) navigate('/a/arche_document_sign');
  }

  /**
   * Signers on a Regulated envelope with no code — stored or typed here.
   *
   * Local state answers this better than the server can at this moment: a code
   * typed seconds ago has not been saved yet, and `hasStoredCode` reflects the
   * last save. Either satisfies the requirement, so the two are OR'd.
   */
  const signersMissingCode = tierRequiresAccessCode(assuranceTier)
    ? signers.filter((s) => !s.hasStoredCode && (accessCodes[s.key] ?? '').trim() === '')
    : [];

  async function handleContinueToReview() {
    if (!envelopeId) return;
    // Save first even when the gate below will block: a sender who filled in
    // three of four codes should not lose the three by being turned back.
    if (!(await flushBeforeLeaving())) return;

    // Caught HERE rather than only on Review, because this button sits a few
    // inches below the code fields — the sender is still looking at the thing
    // they need to fix. Review keeps its own copy of this check: it is a real
    // route, reachable directly and after a round trip through the builder,
    // and Send is the irreversible step.
    if (signersMissingCode.length > 0) {
      const names = signersMissingCode
        .map((s) => s.name.trim() || s.email.trim() || 'an unnamed signer')
        .join(', ');
      setSaveError(
        `This is a Consumer / regulated envelope, so every signer needs an access code. Still missing: ${names}.`,
      );
      return;
    }

    setSaveError(null);
    navigate(`/a/arche_document_sign/envelope/${envelopeId}/review`);
  }

  return (
    <div className="flex flex-col h-full">
      <ArcheHeader archeId="arche_document_sign" title="Document Sign">
        <HolderSwitcher selectedHolderId={activeHolderId} onChange={setActiveHolderId} />
        <Button variant="outline" size="sm" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        {/* Renders only for an envelope handed over by another arche. The
            outcome is `saved_draft`: leaving the builder keeps the draft, so
            claiming `cancelled` here would tell the calling arche nothing was
            created when something was. */}
        <ReturnToOriginButton
          envelope={envelope}
          outcome="saved_draft"
          variant="ghost"
          label="Back to the app that sent this"
          beforeLeave={flushBeforeLeaving}
        />
      </ArcheHeader>
      <div className="max-w-5xl mx-auto p-6 w-full space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold">Envelope</h1>
            <p className="text-xs text-muted-foreground">
              Status: {envelope.status.replace('_', ' ')}
            </p>
          </div>
          {isDraft && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDelete}
              disabled={deleteEnvelope.isPending}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete draft
            </Button>
          )}
        </div>

        {/*
         * Workspace callout. Sits above Step 1 so the user knows up front
         * which workspace the envelope belongs to — moved here from a
         * buried field at the bottom of the form when we dropped the
         * "pick a workspace" create-time dialog. The chosen workspace is
         * fixed for the envelope's lifetime; the only way to change it is
         * delete and recreate.
         */}
        <section
          className="border-l-4 border-primary bg-primary/5 rounded-r-md p-4"
          aria-label="Workspace context"
          data-testid="envelope-workspace-banner"
        >
          <p className="text-sm">
            <span className="text-muted-foreground">Workspace: </span>
            <strong data-testid="envelope-holder">{holderName ?? envelope.holderId}</strong>
            <span className="text-xs text-muted-foreground ml-2">
              ({envelope.holderType === 'org' ? 'Organization' : 'Personal'})
            </span>
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Source documents and your signed copy live in this workspace. Can't be changed later —
            delete this draft and start a new envelope to use a different workspace.
          </p>
        </section>

        <section className="space-y-3">
          <div>
            <h2 className="text-sm font-semibold">Step 1 · Documents</h2>
            <p className="text-xs text-muted-foreground">
              Upload the files your recipients will sign.
            </p>
          </div>
          {envelopeId && !filesLoading && (
            <DocumentGrid envelopeId={envelopeId} files={files} readOnly={!isDraft} />
          )}
          {envelopeId && isDraft && (
            <DocumentUploader envelopeId={envelopeId} currentCount={sourceFiles.length} />
          )}
        </section>

        <section className="space-y-3">
          <div>
            <h2 className="text-sm font-semibold">Step 2 · Recipients</h2>
            <p className="text-xs text-muted-foreground">
              {isBulk
                ? `Add up to ${DOCUMENT_SIGN_MAX_BULK_RECIPIENTS} people — type them in or import a CSV. Each gets their own copy to sign and never sees anyone else on this list.`
                : `Add up to ${DOCUMENT_SIGN_MAX_RECIPIENTS} signers, viewers, or approvers. External signers only need an email.`}
            </p>
          </div>
          {canChooseSendMode && (
            <fieldset
              className="space-y-2"
              disabled={switchingMode || setSendMode.isPending || pendingSwitch !== null}
              aria-busy={switchingMode || setSendMode.isPending}
            >
              <legend className="sr-only">How should this be sent?</legend>
              <div className="flex flex-col gap-2 text-sm sm:flex-row sm:gap-6">
                <label className="flex items-start gap-2">
                  <input
                    type="radio"
                    name="send-mode"
                    value="single"
                    checked={!isBulk}
                    disabled={bulkListTooLongForSingle}
                    onChange={() => void handleSendModeChange('single')}
                    className="mt-1"
                  />
                  <span>
                    <span className="font-medium">Everyone signs one document</span>
                    <span className="block text-xs text-muted-foreground">
                      {bulkListTooLongForSingle
                        ? `One shared document can have at most ${DOCUMENT_SIGN_MAX_RECIPIENTS} people.`
                        : 'All recipients sign the same copy and can see each other.'}
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2">
                  <input
                    type="radio"
                    name="send-mode"
                    value="bulk"
                    checked={isBulk}
                    disabled={bulkBlockedByTier}
                    onChange={() => void handleSendModeChange('bulk')}
                    className="mt-1"
                  />
                  <span>
                    <span className="font-medium">Each person signs their own copy</span>
                    <span className="block text-xs text-muted-foreground">
                      {bulkBlockedByTier
                        ? 'Not available for Consumer / regulated documents, which need an access code per signer.'
                        : 'Bulk send — a separate envelope for each person, charged as one envelope each.'}
                    </span>
                  </span>
                </label>
              </div>
              {sendModeError && <p className="text-sm text-destructive">{sendModeError}</p>}
            </fieldset>
          )}
          {envelopeId && (
            <RecipientsTable
              // Remount on a mode switch: each mode hydrates from a different
              // source (recipients vs the bulk list).
              key={envelope.sendMode ?? 'single'}
              ref={recipientsRef}
              envelopeId={envelopeId}
              envelopeHolderId={envelope.holderId}
              signingOrder={signingOrder}
              accessCodes={accessCodes}
              onSignersChange={setSigners}
              readOnly={editorsLocked}
              bulk={isBulk}
              bulkRecipients={envelope.bulkRecipients ?? null}
            />
          )}
        </section>

        <section className="space-y-3">
          <div>
            <h2 className="text-sm font-semibold">Step 3 · Fields</h2>
            <p className="text-xs text-muted-foreground">
              {isBulk
                ? 'Fields are placed for each recipient — every person fills in the same spots on their own copy. Pick a field type, then click on the page to place it.'
                : 'Pick a recipient and a field type, then click on the page to place it.'}{' '}
              Drag to move, drag the corner to resize.
            </p>
          </div>
          {envelopeId && files.length > 0 && (
            <FieldsEditor
              ref={fieldsRef}
              envelopeId={envelopeId}
              files={files}
              readOnly={editorsLocked}
              bulk={isBulk}
            />
          )}
        </section>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="document-sign-title">Title</Label>
            <Input
              id="document-sign-title"
              value={title}
              maxLength={300}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Q1 Service Agreement"
              disabled={!isDraft}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="document-sign-message">Message to recipients</Label>
            <Textarea
              id="document-sign-message"
              value={message}
              maxLength={5000}
              rows={5}
              onChange={(e) => setMessage(e.target.value)}
              disabled={!isDraft}
            />
          </div>

          {/* Each bulk copy has a single signer, so order means nothing there. */}
          <fieldset className="space-y-2" disabled={!isDraft} hidden={isBulk}>
            <Label>Signing order</Label>
            <div className="flex gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="signing-order"
                  value="parallel"
                  checked={signingOrder === 'parallel'}
                  onChange={() => setSigningOrder('parallel')}
                />
                Any order (parallel)
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="signing-order"
                  value="sequential"
                  checked={signingOrder === 'sequential'}
                  onChange={() => setSigningOrder('sequential')}
                />
                One at a time (sequential)
              </label>
            </div>
          </fieldset>

          <fieldset className="space-y-2" disabled={editorsLocked}>
            <Label>Document type</Label>
            <div className="space-y-2 text-sm">
              <label className="flex items-start gap-2">
                <input
                  type="radio"
                  name="assurance-tier"
                  value="standard"
                  checked={assuranceTier === 'standard'}
                  onChange={() => setAssuranceTier('standard')}
                  className="mt-1"
                />
                <span>
                  <span className="font-medium">Standard</span>
                  <span className="block text-xs text-muted-foreground">
                    Business agreements — contracts, NDAs, internal approvals.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2">
                <input
                  type="radio"
                  name="assurance-tier"
                  value="regulated"
                  checked={assuranceTier === 'regulated'}
                  onChange={() => setAssuranceTier('regulated')}
                  // Needs an access code per signer, which a bulk send can't
                  // carry yet. The server refuses it too.
                  disabled={isBulk}
                  className="mt-1"
                />
                <span>
                  <span className="font-medium">Consumer / regulated</span>
                  <span className="block text-xs text-muted-foreground">
                    Documents a consumer must receive in writing — lending, account and disclosure
                    paperwork. Adds a fuller consent step and requires an access code for every
                    signer.
                    {isBulk && ' Not available for a bulk send.'}
                  </span>
                </span>
              </label>
            </div>
            {/* Named here rather than only at send: the requirement is on the
                recipients, so a sender who learns about it at the final step
                has to go back and redo work they already thought was done. */}
            {tierRequiresAccessCode(assuranceTier) && (
              <div className="space-y-2 pt-1">
                <p className="text-xs rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-amber-900 dark:text-amber-200">
                  Every signer needs an access code before this envelope can be sent.
                </p>
                {/* The codes themselves, right here. They used to be a column
                    in the recipients table three sections up, so this warning
                    named a requirement whose controls were off-screen — the
                    sender had to scroll back through the fields editor to find
                    a narrow input between Role and Delete. */}
                <AccessCodeAssignment
                  signers={signers}
                  codes={accessCodes}
                  onChange={setAccessCodes}
                  disabled={!isDraft}
                />
              </div>
            )}
          </fieldset>

          {saveError && <p className="text-sm text-destructive">{saveError}</p>}

          {isDraft && (
            <div className="flex gap-2">
              <Button onClick={handleContinueToReview}>
                <Send className="h-4 w-4 mr-2" />
                Review and send
              </Button>
            </div>
          )}
        </div>
      </div>

      <AlertDialog
        open={pendingSwitch !== null}
        onOpenChange={(open) => {
          if (!open) setPendingSwitch(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingSwitch?.next === 'bulk'
                ? 'Send a separate copy to each person?'
                : 'Switch to one shared document?'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>Switching changes this draft, and switching back won't undo it:</p>
                <ul className="list-disc pl-5 space-y-1">
                  {pendingSwitch?.losses.map((loss) => (
                    <li key={loss}>{loss}</li>
                  ))}
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it as it is</AlertDialogCancel>
            <AlertDialogAction onClick={() => void confirmPendingSwitch()}>
              Switch
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
